import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';

import { Breadcrumb } from 'components/Breadcrumb';
import { Button } from 'components/button/Button';
import { PageHeader } from 'components/PageHeader';
import { PageHeading } from 'components/PageHeading';
import classNames from '@utils/classnames';

import { AIDraftProvider, useAIDraftCtx } from './context';
import { NameStep } from './NameStep';
import { UploadStep } from './UploadStep';
import { ProcessingStep } from './ProcessingStep';
import { ReviewLanesStep } from './ReviewLanesStep';
import { DraftOfferteStep } from './DraftOfferteStep';
import type { ActiveWindow } from './reducer';

const STEPS: { key: ActiveWindow; label: string; number: number }[] = [
  { key: 'NAME_DRAFT', label: 'Naam', number: 1 },
  { key: 'UPLOAD', label: 'Upload', number: 2 },
  { key: 'PROCESSING', label: 'Extractie', number: 3 },
  { key: 'REVIEW_LANES', label: 'Review lanes', number: 4 },
  { key: 'DRAFT_OFFERTE', label: 'Draft offerte', number: 5 },
];

const StepIndicator = ({ activeWindow }: { activeWindow: ActiveWindow }) => {
  const activeIdx = STEPS.findIndex((s) => s.key === activeWindow);

  return (
    <div className="flex items-center justify-center gap-2 py-4">
      {STEPS.map((step, idx) => {
        const isActive = step.key === activeWindow;
        const isCompleted = idx < activeIdx;

        return (
          <div key={step.key} className="flex items-center gap-2">
            {idx > 0 && <div className="w-16 h-px bg-border" />}
            <div className="flex items-center gap-2">
              <div
                className={classNames('w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium', {
                  'bg-brand text-fg-inverse': isActive,
                  'bg-warning-subtle text-brand-text': isCompleted,
                  'bg-neutral-subtle text-fg-muted': !isActive && !isCompleted,
                })}
              >
                {step.number}
              </div>
              <span
                className={classNames('text-sm font-medium', {
                  'text-fg': isActive,
                  'text-fg-muted': !isActive,
                })}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AIDraftComponent = () => {
  const { state, dispatch } = useAIDraftCtx();

  let canGoForward = false;
  if (state.activeWindow === 'REVIEW_LANES') {
    canGoForward = state.lanes.length > 0 && state.customer !== null;
  }

  const canGoBack = state.activeWindow !== 'NAME_DRAFT' && state.activeWindow !== 'PROCESSING';

  return (
    <>
      <PageHeader title="AI Offerte Draft" />

      <PageHeading
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Offertes',
              to: '/internal/finance/quotations',
            }}
            currentItem="AI Offerte Draft"
          />
        }
      />

      <StepIndicator activeWindow={state.activeWindow} />

      {canGoBack && (
        <div className="flex justify-between px-4 mb-4">
          <Button iconLeft={<CaretLeftIcon className="button-icon" />} onTrigger={() => dispatch({ type: 'GO_BACK' })}>
            Keer terug
          </Button>

          {state.activeWindow === 'REVIEW_LANES' && (
            <Button
              color="primary"
              iconRight={<CaretRightIcon className="button-icon" />}
              isDisabled={!canGoForward}
              onTrigger={() => dispatch({ type: 'GO_FORWARD' })}
            >
              Ga verder
            </Button>
          )}
        </div>
      )}

      {state.activeWindow === 'NAME_DRAFT' && <NameStep />}
      {state.activeWindow === 'UPLOAD' && <UploadStep />}
      {state.activeWindow === 'PROCESSING' && <ProcessingStep />}
      {state.activeWindow === 'REVIEW_LANES' && <ReviewLanesStep />}
      {state.activeWindow === 'DRAFT_OFFERTE' && <DraftOfferteStep />}
    </>
  );
};

const AIDraftPage = () => {
  return (
    <AIDraftProvider>
      <AIDraftComponent />
    </AIDraftProvider>
  );
};

export default AIDraftPage;
